import { SEO } from "components/core/seo"
import { Spinner } from "components/core/spinner"
import { Grid } from "components/dashboard/grid/grid"
import { getOgImage } from "components/dashboard/ogImage"
import { PasswordProtection } from "components/dashboard/passwordProtection"
import { Error404 } from "components/layout/404"
import { Dashboard } from "domainModels/dashboard"
import { supabaseAdmin } from "lib/supabase/admin"
import { getDashboardIdFromSlug } from "lib/utils"
import { GetStaticPaths, GetStaticProps, NextPage } from "next"
import React from "react"
import { useCurrentDashboard } from "reactQuery/dashboard"
import { selectPublicDashboard, selectPublicDashboardsIds } from "repository/dashboard"

interface Props {
  dashboard: Dashboard | null
  dashboardId: number
  isPasswordProtected: boolean
}

const DashboardPage: NextPage<Props> = ({ dashboard, dashboardId, isPasswordProtected }) => {
  const { data: currentDashboard, isLoading: isLoadingCurrentDashboard } = useCurrentDashboard()

  if (dashboard) {
    // We got dashboard data from server so it's a public dashboard
    return (
      <>
        <SEO
          title={dashboard.name}
          description={`A dashboard by ${dashboard.owner_name}`}
          ogImage={getOgImage(dashboard)}
        />
        <Grid dashboard={dashboard} />
      </>
    )
  } else {
    // We got no data from server so it's either private or doesn't exist

    if (isLoadingCurrentDashboard) {
      return (
        <div className="w-full pt-44 md:pt-64 flex items-center justify-center">
          <Spinner className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
        </div>
      )
    }

    if (currentDashboard) {
      return (
        <>
          <SEO
            title={currentDashboard.name}
            description={`A dashboard by ${currentDashboard.owner_name}`}
            ogImage={getOgImage(currentDashboard)}
          />
          <Grid dashboard={currentDashboard} />
        </>
      )
    } else if (isPasswordProtected) {
      return <PasswordProtection dashboardId={dashboardId} />
    }

    return <Error404 />
  }
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// the path has not been generated.
export const getStaticPaths: GetStaticPaths = async () => {
  const ids = await selectPublicDashboardsIds()

  const paths = ids
    ? ids.map((id) => ({
        params: { slug: id.toString() },
      }))
    : []

  // We'll pre-render only these paths at build time.
  // { fallback: blocking } will server-render pages
  // on-demand if the path doesn't exist.
  return {
    paths,
    fallback: "blocking",
  }
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  let publicDashboard: Dashboard | null = null
  let isPasswordProtected = false
  const slug = params?.slug?.toString()

  if (!slug) {
    throw Error("Invalid slug passed")
  }

  const dashboardId = getDashboardIdFromSlug(slug)

  try {
    publicDashboard = await selectPublicDashboard(dashboardId)

    if (!publicDashboard) {
      const { count } = await supabaseAdmin
        .from("dashboard")
        .select("id", { count: "exact" })
        .eq("is_password_protected", true)
        .eq("id", dashboardId)

      isPasswordProtected = count === 1
    }
  } catch (err) {}

  return {
    props: {
      // https://github.com/vercel/next.js/issues/11993
      dashboard: JSON.parse(JSON.stringify(publicDashboard)),
      dashboardId,
      isPasswordProtected,
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 60 seconds
    revalidate: 60,
  }
}

export default DashboardPage
